import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Download,
  Terminal,
  CheckCircle,
  Copy,
  Check,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Monitor,
  Cpu,
  HardDrive,
  Info,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type OS = 'macos' | 'windows' | 'linux' | 'unknown'
type Arch = 'amd64' | 'arm64'

interface PlatformDownload {
  os: OS
  arch: Arch
  label: string
  fileName: string
  fileSize: string
  installCmd?: string
}

// ---------------------------------------------------------------------------
// Version & download metadata
// ---------------------------------------------------------------------------

const CLI_VERSION = '1.1.0'

const DOWNLOADS: PlatformDownload[] = [
  {
    os: 'macos',
    arch: 'arm64',
    label: 'macOS Apple Silicon (M1/M2/M3/M4)',
    fileName: `xarlie-v${CLI_VERSION}-darwin-arm64`,
    fileSize: '~8 MB',
    installCmd: `curl -fsSL https://releases.xarlie.io/v${CLI_VERSION}/xarlie-v${CLI_VERSION}-darwin-arm64 -o xarlie\nchmod +x xarlie\nsudo mv xarlie /usr/local/bin/`,
  },
  {
    os: 'macos',
    arch: 'amd64',
    label: 'macOS Intel',
    fileName: `xarlie-v${CLI_VERSION}-darwin-amd64`,
    fileSize: '~8 MB',
    installCmd: `curl -fsSL https://releases.xarlie.io/v${CLI_VERSION}/xarlie-v${CLI_VERSION}-darwin-amd64 -o xarlie\nchmod +x xarlie\nsudo mv xarlie /usr/local/bin/`,
  },
  {
    os: 'windows',
    arch: 'amd64',
    label: 'Windows x64',
    fileName: `xarlie-v${CLI_VERSION}-windows-amd64.exe`,
    fileSize: '~8 MB',
  },
  {
    os: 'linux',
    arch: 'amd64',
    label: 'Linux x64',
    fileName: `xarlie-v${CLI_VERSION}-linux-amd64`,
    fileSize: '~8 MB',
    installCmd: `curl -fsSL https://releases.xarlie.io/v${CLI_VERSION}/xarlie-v${CLI_VERSION}-linux-amd64 -o xarlie\nchmod +x xarlie\nsudo mv xarlie /usr/local/bin/`,
  },
  {
    os: 'linux',
    arch: 'arm64',
    label: 'Linux ARM64',
    fileName: `xarlie-v${CLI_VERSION}-linux-arm64`,
    fileSize: '~8 MB',
    installCmd: `curl -fsSL https://releases.xarlie.io/v${CLI_VERSION}/xarlie-v${CLI_VERSION}-linux-arm64 -o xarlie\nchmod +x xarlie\nsudo mv xarlie /usr/local/bin/`,
  },
]

const DOWNLOAD_BASE = `https://releases.xarlie.io/v${CLI_VERSION}`
const INSTALL_SCRIPT_URL = 'https://releases.xarlie.io/install.sh'
const INSTALL_CMD = `curl -fsSL ${INSTALL_SCRIPT_URL} | sh`

// ---------------------------------------------------------------------------
// OS detection
// ---------------------------------------------------------------------------

function detectOS(): OS {
  const ua = navigator.userAgent.toLowerCase()
  const platform = (navigator.platform || '').toLowerCase()

  if (ua.includes('mac') || platform.includes('mac')) return 'macos'
  if (ua.includes('win') || platform.includes('win')) return 'windows'
  if (ua.includes('linux') || platform.includes('linux')) return 'linux'
  return 'unknown'
}

function detectArch(): Arch {
  const ua = navigator.userAgent.toLowerCase()
  // Detect Apple Silicon or ARM
  if (ua.includes('arm') || ua.includes('aarch64')) return 'arm64'
  // macOS Safari on Apple Silicon doesn't expose arch in UA,
  // but we can check platform
  const platform = (navigator.platform || '').toLowerCase()
  if (platform === 'macarm' || platform.includes('arm')) return 'arm64'
  return 'amd64'
}

// ---------------------------------------------------------------------------
// SVG Logos
// ---------------------------------------------------------------------------

function AppleLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  )
}

function WindowsLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 12V6.5l8-1.1V12H3zm0 .5h8v6.6l-8-1.1V12.5zm9 0h9V3l-9 1.2V12.5zm0 .5v6.3L21 21v-8H12z" />
    </svg>
  )
}

function LinuxLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.368 1.884 1.43.868.07 1.726-.397 2.403-1.03.736-.654 1.397-1.602 1.397-2.7 0-.26-.037-.467-.129-.666a.61.61 0 00-.032-.063.553.553 0 00-.096-.121l-.007-.007a1.053 1.053 0 00-.283-.166c.091-.256.148-.602.148-1.084 0-.539-.17-1.2-.567-1.8-.61-.9-1.48-1.5-2.18-1.8-.533-.2-1-.267-1.266-.267h-.2c-.133 0-.266.033-.4.066l-.067.02a2.666 2.666 0 00-.867.467c-.467.4-.833.933-1.133 1.533l-.003.007c-.1.2-.2.467-.267.733-.133-.2-.267-.467-.4-.733a10.05 10.05 0 00-.6-1c-.267-.333-.6-.6-.934-.733-.333-.133-.733-.2-1.133-.133-.267.033-.533.133-.8.4-.133.133-.267.333-.333.533 0-.533.033-1.133.2-1.733.267-.867.8-1.733 1.733-2.4.6-.4 1.267-.667 1.667-.867.4-.133.667-.267.933-.467.267-.2.4-.533.467-.8.033-.2.067-.467.033-.733-.033-.267-.133-.533-.333-.733-.2-.2-.467-.333-.733-.4a2.376 2.376 0 00-.267-.066c.267-.733.667-1.333 1.133-1.733.467-.4 1-.6 1.533-.667a2.8 2.8 0 011.267.133c.4.133.8.333 1.133.6.267.2.533.467.733.733.133.2.267.4.333.6.133.267.2.533.2.8 0 .533-.133 1.067-.4 1.467-.133.2-.333.4-.533.533-.267.2-.6.333-1 .4-.4.067-.8.067-1.2-.067a3.15 3.15 0 01-.667-.333c-.133-.133-.333-.2-.467-.4l-.067-.067c-.067.333-.033.733.133 1.067.2.333.467.6.8.8.333.2.733.333 1.133.4.4.067.867.033 1.267-.133s.733-.4 1-.8c.267-.333.467-.8.533-1.267.067-.533 0-1.067-.2-1.533-.2-.4-.467-.733-.8-1s-.733-.467-1.133-.6a3.553 3.553 0 00-1.533-.2c-.733.067-1.467.333-2.067.867-.6.467-1.067 1.133-1.4 1.933l-.033.067c-.267 0-.533.067-.8.133-.333.133-.6.333-.8.6-.2.267-.333.6-.4.933-.067.333-.033.667.033 1-.067-.133-.133-.333-.2-.467-.267-.467-.667-.933-1.133-1.267-.467-.333-1.067-.533-1.733-.467-.333.033-.667.133-.933.333s-.467.467-.6.8c-.133.333-.133.733-.067 1.067l.067.267c-.8.4-1.467.867-2 1.467-.733.733-1.267 1.667-1.533 2.667-.267 1-.267 2.067 0 3.067L5.8 17.4c-.133-.067-.267-.2-.4-.267a.81.81 0 00-.267-.067c-.133 0-.267.067-.4.133-.133.067-.267.2-.333.333C4.133 18 4 18.6 4.067 19.133c.067.6.267 1.133.6 1.533.267.333.667.6 1.067.733.4.133.8.133 1.133 0 .333-.133.667-.4.867-.733a5.85 5.85 0 00.333-.533c.067-.133.133-.267.133-.4 0-.133-.067-.267-.133-.4C8 19.2 7.867 19.067 7.733 19c-.133-.067-.267-.067-.4-.067-.133 0-.267.033-.4.1l-.067.033c-.2-.267-.333-.6-.4-.933-.067-.4 0-.8.2-1.067.067-.067.133-.133.2-.2.2.4.467.733.8.933.4.267.867.4 1.333.4.467 0 .933-.133 1.333-.4.4-.267.733-.667.933-1.133.2-.467.267-1 .2-1.533-.067-.467-.267-.933-.6-1.333C10.533 13.2 10.067 12.867 9.533 12.667c-.533-.2-1.133-.2-1.667 0-.067.033-.133.067-.2.1.067-.333.2-.667.4-.933l.067-.1a6.3 6.3 0 011.733-1.8c.4-.267.867-.467 1.333-.533z" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const OS_CONFIG = {
  macos: {
    name: 'macOS',
    Logo: AppleLogo,
    color: 'gray',
    bgClass: 'bg-gray-900',
    textClass: 'text-white',
    hoverClass: 'hover:bg-gray-800',
    badgeClass: 'bg-gray-100 text-gray-800',
    ringClass: 'ring-gray-300',
    requirements: [
      { icon: Monitor, text: 'macOS 11 Big Sur or later' },
      { icon: Cpu, text: 'Apple Silicon (M1+) or Intel x64' },
      { icon: HardDrive, text: '~20 MB disk space' },
    ],
  },
  windows: {
    name: 'Windows',
    Logo: WindowsLogo,
    color: 'blue',
    bgClass: 'bg-blue-600',
    textClass: 'text-white',
    hoverClass: 'hover:bg-blue-700',
    badgeClass: 'bg-blue-100 text-blue-800',
    ringClass: 'ring-blue-300',
    requirements: [
      { icon: Monitor, text: 'Windows 10 version 1809 or later' },
      { icon: Cpu, text: 'x64 or ARM64 processor' },
      { icon: HardDrive, text: '~20 MB disk space' },
    ],
  },
  linux: {
    name: 'Linux',
    Logo: LinuxLogo,
    color: 'orange',
    bgClass: 'bg-orange-500',
    textClass: 'text-white',
    hoverClass: 'hover:bg-orange-600',
    badgeClass: 'bg-orange-100 text-orange-800',
    ringClass: 'ring-orange-300',
    requirements: [
      { icon: Monitor, text: 'Any modern Linux distro (kernel 4.15+)' },
      { icon: Cpu, text: 'x64 or ARM64 processor' },
      { icon: HardDrive, text: '~20 MB disk space' },
    ],
  },
  unknown: {
    name: 'Your OS',
    Logo: Monitor,
    color: 'gray',
    bgClass: 'bg-gray-600',
    textClass: 'text-white',
    hoverClass: 'hover:bg-gray-700',
    badgeClass: 'bg-gray-100 text-gray-800',
    ringClass: 'ring-gray-300',
    requirements: [],
  },
} as const

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DownloadsPage() {
  const [detectedOS, setDetectedOS] = useState<OS>('unknown')
  const [detectedArch, setDetectedArch] = useState<Arch>('amd64')
  const [showAllPlatforms, setShowAllPlatforms] = useState(false)
  const [copiedCmd, setCopiedCmd] = useState<string | null>(null)

  useEffect(() => {
    setDetectedOS(detectOS())
    setDetectedArch(detectArch())
  }, [])

  // Best match for current OS+arch
  const recommended = useMemo(() => {
    if (detectedOS === 'unknown') return DOWNLOADS[0]
    const exact = DOWNLOADS.find(
      (d) => d.os === detectedOS && d.arch === detectedArch
    )
    if (exact) return exact
    return DOWNLOADS.find((d) => d.os === detectedOS) || DOWNLOADS[0]
  }, [detectedOS, detectedArch])

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedCmd(id)
    setTimeout(() => setCopiedCmd(null), 2000)
  }

  const cfg = OS_CONFIG[detectedOS]

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-20" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-500/20 text-primary-300 mb-6">
            v{CLI_VERSION}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
            Download Xarlie CLI
          </h1>
          <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">
            Install the command-line interface to discover, install, and manage MCP
            skills for your AI agents.
          </p>
        </div>
      </section>

      {/* Quick install */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="p-6 sm:p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              Quick Install
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Auto-detects your OS and architecture. Works on macOS, Linux, and WSL.
            </p>
            <div className="relative bg-gray-900 rounded-xl p-4 font-mono text-sm text-gray-300">
              <pre>{INSTALL_CMD}</pre>
              <button
                onClick={() => copyToClipboard(INSTALL_CMD, 'install-script')}
                className="absolute top-3 right-3 p-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                title="Copy to clipboard"
              >
                {copiedCmd === 'install-script' ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              You can also set a specific version:{' '}
              <code className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">
                curl -fsSL {INSTALL_SCRIPT_URL} | XARLIE_VERSION=v{CLI_VERSION} sh
              </code>
            </p>
          </div>
        </div>
      </section>

      {/* Recommended download */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Detected OS banner */}
          <div className="px-6 py-4 bg-gradient-to-r from-primary-50 to-accent-50 border-b border-gray-100 flex items-center gap-2">
            <Info className="w-4 h-4 text-primary-600 flex-shrink-0" />
            <span className="text-sm text-gray-700">
              Or download the binary directly for{' '}
              <strong>{cfg.name}</strong>.
            </span>
          </div>

          {/* Main download card */}
          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              {/* OS Logo + info */}
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div
                  className={`w-16 h-16 rounded-2xl flex items-center justify-center ${cfg.bgClass} ${cfg.textClass} flex-shrink-0`}
                >
                  <cfg.Logo className="w-9 h-9" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-gray-900">
                    Xarlie CLI for {cfg.name}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {recommended.label} &middot; {recommended.fileSize}
                  </p>
                </div>
              </div>

              {/* Download button */}
              <a
                href={`${DOWNLOAD_BASE}/${recommended.fileName}`}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-base font-semibold shadow-lg transition-all ${cfg.bgClass} ${cfg.textClass} ${cfg.hoverClass} flex-shrink-0`}
              >
                <Download className="w-5 h-5" />
                Download for {cfg.name}
              </a>
            </div>

            {/* Manual install command */}
            {recommended.installCmd && (
              <div className="mt-6">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Manual install via terminal:
                </p>
                <div className="relative bg-gray-900 rounded-xl p-4 font-mono text-sm text-gray-300 overflow-x-auto">
                  <pre className="whitespace-pre-wrap break-all">
                    {recommended.installCmd}
                  </pre>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        recommended.installCmd!,
                        'recommended'
                      )
                    }
                    className="absolute top-3 right-3 p-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                    title="Copy to clipboard"
                  >
                    {copiedCmd === 'recommended' ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Windows-specific install note */}
            {recommended.os === 'windows' && (
              <div className="mt-6">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Installation steps:
                </p>
                <ol className="text-sm text-gray-600 space-y-1.5 list-decimal list-inside">
                  <li>Download the <code className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-800 font-mono text-xs">xarlie.exe</code> file</li>
                  <li>
                    Move it to a folder in your PATH (e.g.{' '}
                    <code className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-800 font-mono text-xs">
                      C:\Users\YourName\bin
                    </code>
                    )
                  </li>
                  <li>
                    Or add the download folder to your PATH environment variable
                  </li>
                  <li>
                    Open a new terminal and run{' '}
                    <code className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-800 font-mono text-xs">
                      xarlie --help
                    </code>
                  </li>
                </ol>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Verify installation */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            Verify Installation
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            After installing, open a new terminal and run:
          </p>
          <div className="mt-3 relative bg-gray-900 rounded-xl p-4 font-mono text-sm text-gray-300">
            <pre>xarlie --help</pre>
            <button
              onClick={() => copyToClipboard('xarlie --help', 'verify')}
              className="absolute top-3 right-3 p-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
              title="Copy to clipboard"
            >
              {copiedCmd === 'verify' ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            You should see the Xarlie CLI help output listing all available
            commands.
          </p>
        </div>
      </section>

      {/* System requirements for detected OS */}
      {cfg.requirements.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
            <h3 className="text-lg font-bold text-gray-900">
              System Requirements &mdash; {cfg.name}
            </h3>
            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              {cfg.requirements.map((req, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-xl bg-gray-50"
                >
                  <req.icon className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{req.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All platforms */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <button
          onClick={() => setShowAllPlatforms(!showAllPlatforms)}
          className="w-full flex items-center justify-between bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:bg-gray-50 transition-colors"
        >
          <span className="text-lg font-bold text-gray-900">
            All Platforms &amp; Architectures
          </span>
          {showAllPlatforms ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {showAllPlatforms && (
          <div className="mt-2 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden divide-y divide-gray-100">
            {/* Table header */}
            <div className="hidden sm:grid sm:grid-cols-12 gap-4 px-6 py-3 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              <div className="col-span-5">Platform</div>
              <div className="col-span-3">File</div>
              <div className="col-span-2">Size</div>
              <div className="col-span-2 text-right">Download</div>
            </div>

            {DOWNLOADS.map((dl, i) => {
              const osCfg = OS_CONFIG[dl.os]
              const isRecommended = dl === recommended
              return (
                <div
                  key={i}
                  className={`grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-6 py-4 items-center ${
                    isRecommended ? 'bg-primary-50/40' : ''
                  }`}
                >
                  <div className="sm:col-span-5 flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${osCfg.bgClass} ${osCfg.textClass} flex-shrink-0`}
                    >
                      <osCfg.Logo className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 text-sm">
                        {dl.label}
                      </span>
                      {isRecommended && (
                        <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Recommended
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="sm:col-span-3">
                    <code className="text-xs text-gray-500 font-mono break-all">
                      {dl.fileName}
                    </code>
                  </div>
                  <div className="sm:col-span-2 text-sm text-gray-500">
                    {dl.fileSize}
                  </div>
                  <div className="sm:col-span-2 sm:text-right">
                    <a
                      href={`${DOWNLOAD_BASE}/${dl.fileName}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download
                    </a>
                  </div>
                </div>
              )
            })}

            {/* Checksums row */}
            <div className="px-6 py-4 bg-gray-50">
              <a
                href={`${DOWNLOAD_BASE}/checksums.txt`}
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                SHA256 checksums
              </a>
            </div>
          </div>
        )}
      </section>

      {/* Build from source */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-16">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Terminal className="w-5 h-5 text-gray-600" />
            Build from Source
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            Requires <strong>Go 1.22</strong> or later.{' '}
            <a
              href="https://go.dev/dl/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700"
            >
              Install Go
            </a>
          </p>
          <div className="mt-3 relative bg-gray-900 rounded-xl p-4 font-mono text-sm text-gray-300 overflow-x-auto">
            <pre className="whitespace-pre-wrap">{`git clone https://github.com/xarlie-io/xarlie.git
cd xarlie
go build -o xarlie ./cmd/xarlie
sudo mv xarlie /usr/local/bin/`}</pre>
            <button
              onClick={() =>
                copyToClipboard(
                  'git clone https://github.com/xarlie-io/xarlie.git\ncd xarlie\ngo build -o xarlie ./cmd/xarlie\nsudo mv xarlie /usr/local/bin/',
                  'source'
                )
              }
              className="absolute top-3 right-3 p-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
              title="Copy to clipboard"
            >
              {copiedCmd === 'source' ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* All platform requirements summary */}
          <div className="mt-8">
            <h4 className="text-base font-semibold text-gray-900 mb-4">
              System Requirements by Platform
            </h4>
            <div className="grid md:grid-cols-3 gap-4">
              {(['macos', 'windows', 'linux'] as const).map((os) => {
                const c = OS_CONFIG[os]
                return (
                  <div key={os} className="rounded-xl border border-gray-200 overflow-hidden">
                    <div
                      className={`px-4 py-3 flex items-center gap-2 ${c.bgClass} ${c.textClass}`}
                    >
                      <c.Logo className="w-5 h-5" />
                      <span className="font-semibold text-sm">{c.name}</span>
                    </div>
                    <div className="px-4 py-3 space-y-2">
                      {c.requirements.map((req, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <req.icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-gray-600">
                            {req.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Next steps */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <h4 className="text-base font-semibold text-gray-900 mb-3">
              Next Steps
            </h4>
            <div className="grid sm:grid-cols-3 gap-3">
              <Link
                to="/docs/getting-started"
                className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-sm font-medium text-gray-700 hover:text-primary-700"
              >
                <Terminal className="w-4 h-4" />
                Getting Started Guide
              </Link>
              <Link
                to="/docs/cli"
                className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-sm font-medium text-gray-700 hover:text-primary-700"
              >
                <Terminal className="w-4 h-4" />
                CLI Reference
              </Link>
              <Link
                to="/browse"
                className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-sm font-medium text-gray-700 hover:text-primary-700"
              >
                <Download className="w-4 h-4" />
                Browse Skills
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
